import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import verificationApi from '../api/verificationApi';
import { VerificationStatus, LoadingStatus } from '../../types';

interface VerificationState {
  capturedImage: string | null;
  prompt: string | null;
  promptStatus: LoadingStatus;
  verificationStatus: VerificationStatus | null;
  submissionStatus: LoadingStatus;
  error: string | null;
}

const initialState: VerificationState = {
  capturedImage: null,
  prompt: null,
  promptStatus: 'idle',
  verificationStatus: null,
  submissionStatus: 'idle',
  error: null,
};

export const fetchVerificationPrompt = createAsyncThunk(
  'verification/fetchPrompt',
  async (_, { rejectWithValue }) => {
    try {
      const res = await verificationApi.getPrompt();
      return res.data.prompt as string;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to get prompt');
    }
  },
);

export const submitVerification = createAsyncThunk(
  'verification/submit',
  async ({ imageDataUrl, prompt }: { imageDataUrl: string; prompt: string }, { rejectWithValue }) => {
    try {
      return await verificationApi.submitVerification(imageDataUrl, prompt);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Submission failed');
    }
  },
);

export const fetchVerificationStatus = createAsyncThunk(
  'verification/fetchStatus',
  async (_, { rejectWithValue }) => {
    try {
      const res = await verificationApi.getStatus();
      return res.data.verificationStatus as VerificationStatus;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to fetch status');
    }
  },
);

const verificationSlice = createSlice({
  name: 'verification',
  initialState,
  reducers: {
    setCapturedImage(state, action: PayloadAction<string>) {
      state.capturedImage = action.payload;
    },
    clearCapturedImage(state) {
      state.capturedImage = null;
      state.prompt = null;
    },
    clearVerificationError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVerificationPrompt.pending, (state) => {
        state.promptStatus = 'loading';
      })
      .addCase(fetchVerificationPrompt.fulfilled, (state, { payload }) => {
        state.prompt = payload;
        state.promptStatus = 'succeeded';
      })
      .addCase(fetchVerificationPrompt.rejected, (state) => {
        state.promptStatus = 'failed';
      })
      .addCase(submitVerification.pending, (state) => {
        state.submissionStatus = 'loading';
        state.error = null;
      })
      .addCase(submitVerification.fulfilled, (state) => {
        state.submissionStatus = 'succeeded';
        state.verificationStatus = 'PENDING';
        state.capturedImage = null;
        state.prompt = null;
      })
      .addCase(submitVerification.rejected, (state, { payload }) => {
        state.submissionStatus = 'failed';
        state.error = payload as string;
      })
      .addCase(fetchVerificationStatus.fulfilled, (state, { payload }) => {
        state.verificationStatus = payload;
      });
  },
});

export const { setCapturedImage, clearCapturedImage, clearVerificationError } = verificationSlice.actions;
export default verificationSlice.reducer;

export const selectVerificationStatus = (state: { verification: VerificationState }) =>
  state.verification.verificationStatus;
export const selectCapturedImage = (state: { verification: VerificationState }) =>
  state.verification.capturedImage;
export const selectSubmissionStatus = (state: { verification: VerificationState }) =>
  state.verification.submissionStatus;
export const selectVerificationPrompt = (state: { verification: VerificationState }) =>
  state.verification.prompt;
export const selectPromptStatus = (state: { verification: VerificationState }) =>
  state.verification.promptStatus;
